import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  AcademicCapIcon, 
  ClipboardDocumentListIcon, 
  ChartBarIcon,
  UserIcon,
  BookOpenIcon,
  DocumentTextIcon,
  ClockIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

const Home: React.FC = () => {
  const { user } = useAuth();

  const features = [
    {
      name: '在线练习',
      description: '随机题目练习，实时反馈，智能分析薄弱环节',
      icon: AcademicCapIcon,
      href: '/practice',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-700'
    },
    {
      name: '模拟考试',
      description: '限时考试，自动批改，详细成绩分析',
      icon: ClipboardDocumentListIcon,
      href: '/exams',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-700'
    },
    {
      name: '学习分析',
      description: '详细统计，进度跟踪，个性化建议',
      icon: ChartBarIcon,
      href: '/analytics',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-700'
    }
  ];

  const systemFeatures = [
    {
      title: '🔐 用户认证系统',
      items: ['用户注册/登录功能', '角色权限管理 (学生/教师/管理员)', 'JWT令牌认证', '个人信息管理']
    },
    {
      title: '📚 题库管理系统',
      items: ['支持5种题目类型', '题库分类管理', '难度等级分类', '标签系统']
    },
    {
      title: '🏃 在线练习系统',
      items: ['随机题目生成', '实时答案提交和反馈', '练习进度实时保存', '学习进度跟踪']
    },
    {
      title: '⏰ 考试系统',
      items: ['考试创建和管理', '限时考试功能', '自动提交机制', '考试权限控制']
    }
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          欢迎来到在线刷题系统
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          智能刷题，高效学习，全面提升你的知识水平
        </p>
        
        {!user && (
          <div className="flex justify-center space-x-4">
            <Link
              to="/register"
              className="bg-primary-600 text-white px-6 py-3 rounded-md text-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              立即注册
            </Link>
            <Link
              to="/login"
              className="bg-white text-primary-600 px-6 py-3 rounded-md text-lg font-medium border border-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              登录
            </Link>
          </div>
        )}
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <div key={feature.name} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="text-center">
                <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-md ${feature.color} text-white mb-4`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{feature.name}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <Link
                  to={feature.href}
                  className={`w-full ${feature.color} ${feature.hoverColor} text-white py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 inline-block text-center`}
                >
                  开始使用
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* System Features */}
      <div className="bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">系统特色</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {systemFeatures.map((feature, index) => (
            <div key={index}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{feature.title}</h3>
              <ul className="space-y-2 text-gray-600">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <span className="text-primary-500 mr-2">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Section (if user is logged in) */}
      {user && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">学习统计</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500 rounded-md">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">总练习次数</p>
                  <p className="text-2xl font-bold text-gray-900">0</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-500 rounded-md">
                  <TrophyIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">平均正确率</p>
                  <p className="text-2xl font-bold text-gray-900">0%</p>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-500 rounded-md">
                  <ClockIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">学习时长</p>
                  <p className="text-2xl font-bold text-gray-900">0分钟</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-500 rounded-md">
                  <BookOpenIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">掌握程度</p>
                  <p className="text-2xl font-bold text-gray-900">0%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
